import {Box, Tab, Tabs, Typography} from '@mui/material';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../common/arr-button/ArrButton';
import {t} from 'i18next';
import './BulkTagUntag.scss';
import {TabData, TabPanelProps} from '../../user-management/edit-user/EditUser';
import {useEffect, useMemo, useRef, useState} from 'react';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import {
  existingObjErrCode,
  MAX_FOLDER_NAME_LENGTH,
  PROPOGATION_TYPE,
  RoleId,
  TAG_NAME_REGEX,
  ToastType,
  transition_time_default,
} from '../../../shared/constants';
import ExistingTag from './existing-tag/ExistingTag';
import NewTag from './new-tag/NewTag';
import {RootState} from '../../../redux/store';
import {useSelector} from 'react-redux';
import {ISelectItem} from '../../../shared/types';
import {useGetBulkTagSelectedDocCount} from '../../../hooks/assignments/useAssignments';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {useAddTag} from '../../../hooks/tagsFoldering/useTagsFoldering';
import {numberFormatter} from '../../../utils/utils';
import {ActionSelection} from '../../doc-explorer/ViewDocExplorer';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ToastMessageConfigType} from '../../../api/types/ingestionApi.type';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_EVENT_NAMES,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      className="tabpanel"
      {...other}
      data-testid={`bulk-tag-untag-tab-panel-${index}`}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

const BulkTagUntag: React.FC<{
  isOpen: boolean;
  setOpenActionMenu: (val: boolean) => void;
  handleBulkTagContinue: (
    val: string,
    docCount?: number,
    newTagId?: number,
    validIds?: string[]
  ) => void;
  masterDocListId: number;
  setSelectedNewExistingTag: (value: ActionSelection | null) => void;
  nonBgStandardCount?: number;
  isNonbg?: boolean;
  isManageReviewers?: boolean;
}> = ({
  isOpen,
  setOpenActionMenu,
  handleBulkTagContinue,
  masterDocListId,
  setSelectedNewExistingTag,
  nonBgStandardCount,
  isNonbg,
  isManageReviewers,
}) => {
  const enBulkTag = 'actionMenu.bulkTagFlyout';
  const titleId = 'bulk-tag-title';
  const {trackGAEvent} = useAnalytics();
  const hasTrackedSubpageView = useRef(false);
  const radioOptions = [
    {
      label: t(`${enBulkTag}.tagDocs`),
      value: 'tagDocs',
    },
    {
      label: t(`${enBulkTag}.untagDocs`),
      value: 'untagDocs',
    },
  ];

  const {mutate: addTag, isPending: isAddTagLoading} = useAddTag((res) => {
    const error = res?.errorText;
    if (error?.includes(existingObjErrCode)) setIsTagExist(true);
    else handleBulkTagContinue(selectedAction, totalSelectedDocs, res.tagId);
  });
  const continueRef = useRef<HTMLButtonElement>(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedAction, setSelectedAction] = useState('tagDocs');
  const [roleId, setRoleId] = useState<number>();
  const [selectedExistingTags, setSelectedExistingTags] = useState<
    string[] | null
  >();
  const [selectedNewTag, setSelectedNewTag] = useState<string | null>();
  const [searchInputExistingTags, setSearchInputExistingTags] =
    useState<string>('');
  const [searchInputNewTags, setSearchInputNewTags] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [isNameValid, setIsNameValid] = useState<boolean>(true);
  const [isNameMaxLengthValid, setIsNameMaxLengthValid] =
    useState<boolean>(true);
  const [isTagExist, setIsTagExist] = useState<boolean>(false);
  const [touchedFields, setTouchedFields] = useState<{
    name: boolean;
  }>({
    name: false,
  });
  const [propagateTag, setPropagateTag] = useState<ISelectItem[]>([]);
  const [tagClassification, setTagClassification] =
    useState<ISelectItem | null>(null);
  const [totalSelectedDocs, setTotalSelectedDocs] = useState<number>(0);
  const [securityGroupId, setSecurityGroupId] = useState<number>(0);
  const [tagTreeData, setTagTreeData] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });

  const shouldFetchCount = !isNonbg && masterDocListId > 0;

  const {data, isFetching} = useGetBulkTagSelectedDocCount(
    {masterDocListId},
    shouldFetchCount
  );

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    setName(value);
    setIsTagExist(false);
    if (value.trim() !== '') {
      const isValid = TAG_NAME_REGEX.test(value.trim());
      setIsNameValid(isValid);
      setIsNameMaxLengthValid(value.length <= MAX_FOLDER_NAME_LENGTH);
    } else {
      setIsNameValid(false);
      setIsNameMaxLengthValid(true);
    }
  };
  const handleBlurName = (fieldName: keyof typeof touchedFields) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };
  const setErrorMsg = () => {
    if (touchedFields.name && !name) {
      return t(`${enBulkTag}.errors.nameRequired`);
    }
    if (!isNameValid && name) {
      return t(`${enBulkTag}.errors.nameValidationMsg`);
    }
    if (!isNameMaxLengthValid) {
      return t(`${enBulkTag}.errors.maxLengthMsg`, {
        max: MAX_FOLDER_NAME_LENGTH,
      });
    }
    if (isTagExist) {
      return t(`${enBulkTag}.errors.nameExist`);
    }
    return '';
  };

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  useEffect(() => {
    if (loginUserData) {
      setRoleId(loginUserData?.roleId);
      setSecurityGroupId(loginUserData?.securityGroupId);
    }
  }, [loginUserData]);

  useEffect(() => {
    setSelectedAction('tagDocs');
  }, []);

  useEffect(() => {
    if (tabData.length === 1) {
      setTabValue(0);
    }
  }, [selectedAction]);

  useEffect(() => {
    if (isOpen) {
      continueRef.current?.focus();
    } else {
      setSelectedExistingTags([]);
      setTabValue(0);
      setName('');
      setSelectedNewTag(null);
      setSearchInputExistingTags('');
      setSearchInputNewTags('');
      setTouchedFields({name: false});
      setIsTagExist(false);
      setPropagateTag([]);
      setTagClassification(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isNonbg) {
      if (data?.docCount !== undefined && data.isLikeDocListGenerated) {
        setTotalSelectedDocs(data.docCount);
      } else if (isManageReviewers && nonBgStandardCount !== undefined) {
        setTotalSelectedDocs(nonBgStandardCount);
      }
    } else if (isNonbg && nonBgStandardCount !== undefined) {
      setTotalSelectedDocs(nonBgStandardCount);
    }
  }, [isNonbg, data?.docCount, data?.isLikeDocListGenerated, nonBgStandardCount]);

  useEffect(() => {
    if (!isOpen) {
      hasTrackedSubpageView.current = false;
      return;
    }

    if (hasTrackedSubpageView.current) return;

    hasTrackedSubpageView.current = true;
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.global.tagUntagDocumentsSubpageName'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
        subpage_details: t('gaTracking.global.documentFetchInProgress'),
      },
    });
  }, [isOpen]);

  useEffect(() => {
    if (selectedAction === 'untagDocs' || tabValue === 0) {
      if (selectedExistingTags && selectedExistingTags.length > 0) {
        setSelectedNewExistingTag({
          type: 'existing',
          ids: selectedExistingTags,
        });
      } else {
        setSelectedNewExistingTag(null);
      }
    } else if (tabValue === 1) {
      if (selectedNewTag) {
        setSelectedNewExistingTag({type: 'new', ids: [selectedNewTag]});
      } else {
        setSelectedNewExistingTag(null);
      }
    }
  }, [selectedAction, tabValue, selectedExistingTags, selectedNewTag]);

  const isNameInputValid =
    !isNameValid ||
    !isNameMaxLengthValid ||
    (touchedFields.name && !name) ||
    isTagExist;
  const tabData: TabData[] = [
    {
      id: 'existing-tag',
      label: (
        <Typography
          className="typo-text subhead5_size tab-label"
          data-testid="existing-tag-label"
          component="h3"
        >
          {t(`${enBulkTag}.existingTag`)}
        </Typography>
      ),
      content: (
        <ExistingTag
          selectedExistingTags={selectedExistingTags || []}
          setSelectedExistingTags={setSelectedExistingTags}
          searchInputExistingTags={searchInputExistingTags}
          setSearchInputExistingTags={setSearchInputExistingTags}
          setTagTreeData={setTagTreeData}
        />
      ),
    },
    {
      id: 'new-tag',
      label: (
        <Box className="functionality-tab-label">
          <Typography
            className="typo-text subhead5_size tab-label"
            data-testid="new-tag-label"
            component="h3"
          >
            {t(`${enBulkTag}.newTag`)}
          </Typography>
        </Box>
      ),
      content: (
        <NewTag
          selectedNewTag={selectedNewTag || ''}
          setSelectedNewTag={setSelectedNewTag}
          searchInputNewTags={searchInputNewTags}
          setSearchInputNewTags={setSearchInputNewTags}
          name={name}
          handleChangeName={handleChangeName}
          isNameInputValid={isNameInputValid}
          handleBlurName={handleBlurName}
          setErrorMsg={setErrorMsg}
          propagateTag={propagateTag}
          setPropagateTag={setPropagateTag}
          tagClassification={tagClassification}
          setTagClassification={setTagClassification}
        />
      ),
    },
  ];
  const handleChange = (event: React.SyntheticEvent, newTabValue: number) => {
    setTabValue(newTabValue);
  };

  const tagMap = useMemo(() => {
    const map = new Map<string, {isGroup: boolean; hasChildren: boolean}>();

    const traverse = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
      nodes.forEach((node) => {
        const hasChildren = !!node.children?.length;

        map.set(node.id, {
          isGroup: !!node.isGroup,
          hasChildren,
        });

        if (node.children) {
          traverse(node.children);
        }
      });
    };

    traverse(tagTreeData);
    return map;
  }, [tagTreeData]);

  const handleContinue = () => {
    if (tabValue === 1 && selectedAction === 'tagDocs') {
      const selectedIds = new Set(propagateTag.map((item) => item.id));

      const newPropagation = {
        PropagationToFamily: selectedIds.has(PROPOGATION_TYPE.FAMILY),
        PropagationToDuplicates: selectedIds.has(PROPOGATION_TYPE.DUPLICATES),
        PropagationToEmailThreads: selectedIds.has(
          PROPOGATION_TYPE.EMAILTHREADS
        ),
        PropagationToEmailDuplicates: selectedIds.has(
          PROPOGATION_TYPE.EMAILDUPLICATES
        ),
      };

      const payload = {
        createNewCalModel: false,
        tagData: {
          Name: name.trim(),
          IsGroup: false,
          MasterDocListID: isNonbg ? 0 : masterDocListId,
          SecurityGroupID: securityGroupId.toString(),
          ParentID: selectedNewTag ?? '0',
          IsAIEnabled: false,
          ShowAIHints: false,
          ...newPropagation,
          ...(tagClassification?.id
            ? {
                ClassificationID: tagClassification.id,
                Classification: tagClassification.name ?? '',
              }
            : {}),
        },
      };

      addTag(payload);
    } else {
      const validIds: string[] = [];
      selectedExistingTags?.forEach((id) => {
        const tag = tagMap.get(id);
        if (!tag) return;
        // INVALID: group without children
        const isInvalid = tag.isGroup && !tag.hasChildren;

        if (!isInvalid) {
          validIds.push(id);
        }
      });

      if (validIds.length === 0) {
        if (selectedAction === 'tagDocs') {
          setToastMsgConfig({
            show: true,
            title: t('common.warning'),
            type: ToastType.Warning,
            message: t(`${enBulkTag}.emptyGroupValidationForTag`),
          });
        } else {
          setToastMsgConfig({
            show: true,
            title: t('common.warning'),
            type: ToastType.Warning,
            message: t(`${enBulkTag}.emptyGroupValidationForUnTag`),
          });
        }
        return;
      }
      setSelectedNewExistingTag({
        type: 'existing',
        ids: validIds,
      });
      handleBulkTagContinue(
        selectedAction,
        totalSelectedDocs,
        undefined,
        validIds
      );
    }
  };

  const isContinue = () => {
    if (isAddTagLoading) return false;
    if (totalSelectedDocs <= 0) return false;
    if (selectedAction === 'untagDocs' || tabValue === 0) {
      return Boolean(selectedExistingTags && selectedExistingTags.length > 0);
    } else if (tabValue === 1) {
      return Boolean(selectedNewTag && !isNameInputValid && name);
    }
    return false;
  };
  return (
    <>
      {isOpen && (
        <ArrDrawer
          transition={transition_time_default}
          showButton={false}
          isOpen={isOpen}
          size="large"
          side="right"
          title=""
          showHeader={false}
          aria-labelledby={titleId}
          className="bulk-tag-flyout"
          onClose={() => {
            setOpenActionMenu(false);
          }}
        >
          <Box className="bulk-tag-header-box">
            <Box className="title-box">
              <Typography
                component="h2"
                id={titleId}
                className=" typo-text headline3_size title"
                data-testid="flyout-title"
                tabIndex={0}
              >
                {t(`${enBulkTag}.flyoutTitle`)}
              </Typography>
            </Box>
            <Box className="action-buttons-box">
              <ArrButton
                variant="secondary"
                size="medium"
                onClick={() => {
                  setOpenActionMenu(false);
                }}
                disabled={false}
                aria-label={t('common.cancel')}
                dataTestId="bulk-tag-untag-cancel-button"
              >
                {t('common.cancel')}
              </ArrButton>
              <ArrButton
                ref={continueRef}
                className={isContinue() ? '' : 'btn-disabled'}
                variant="primary"
                size="medium"
                type="submit"
                aria-label={t('common.continue')}
                dataTestId="bulk-tag-untag-continue-button"
                isAriaDisabled={!isContinue()}
                onClick={handleContinue}
                tabIndex={0}
              >
                {t('common.continue')}
              </ArrButton>
            </Box>
          </Box>
          <Box className="bulk-tag-flyout-content">
            <Box className="tag-untag-selection-box">
              <Box className="total-docs" tabIndex={0}>
                <Typography
                  component="span"
                  className="typo-text subhead4_size title"
                  data-testid="total-docs-desc"
                >
                  {t(`${enBulkTag}.totalDocs`)}&nbsp;
                </Typography>
                <Typography
                  component="span"
                  className="typo-text subhead4_size count"
                  data-testid="total-docs"
                >
                  {(!isManageReviewers && isFetching) ||
                  (!data?.isLikeDocListGenerated &&
                    nonBgStandardCount === undefined) ? (
                    <ArrLoadingSpinner
                      className="arr-loading-small"
                      aria-label={t(`${enBulkTag}.loadingDocCount`)}
                    />
                  ) : (
                    numberFormatter.format(totalSelectedDocs)
                  )}
                </Typography>
              </Box>
              <Box className="radio-selection-box">
                <ArrRadio
                  dataTestId="bulk-folder-unfolder-radio-button"
                  name="controlled-radio-buttons-group"
                  size="small"
                  onChange={(value) => {
                    setSelectedAction(value);
                  }}
                  value={selectedAction}
                  data={radioOptions}
                />
              </Box>
            </Box>
            <Box className="tabs-section">
              {roleId?.toString() === RoleId.Reviewer ||
              selectedAction === 'untagDocs' ? (
                <div
                  className="untag-docs-tab-panel"
                  data-testid="existing-tag-wrapper"
                >
                  <ExistingTag
                    selectedExistingTags={selectedExistingTags || []}
                    setSelectedExistingTags={setSelectedExistingTags}
                    searchInputExistingTags={searchInputExistingTags}
                    setSearchInputExistingTags={setSearchInputExistingTags}
                    setTagTreeData={setTagTreeData}
                  />
                </div>
              ) : (
                <>
                  <Tabs
                    value={tabValue || 0}
                    onChange={handleChange}
                    aria-label={t(`${enBulkTag}.tabSelection`)}
                    className="tabs"
                  >
                    {tabData.map((tab) => (
                      <Tab
                        id={tab.id}
                        aria-controls={`tabpanel-${tab.id}`}
                        key={tab.id}
                        label={tab.label}
                        className="tab"
                        data-testid={tab.id}
                      />
                    ))}
                  </Tabs>
                  {tabData.map((tab, index) => (
                    <TabPanel key={tab.id} value={tabValue || 0} index={index}>
                      {tab.content}
                    </TabPanel>
                  ))}
                </>
              )}
            </Box>
          </Box>
          {toastMsgConfig.show && (
            <ArrToastNotification
              onClose={() => setToastMsgConfig({show: false})}
              status={[toastMsgConfig.type ?? ToastType.Success]}
              message={toastMsgConfig.message ?? ''}
              title={toastMsgConfig.title}
            />
          )}
        </ArrDrawer>
      )}
    </>
  );
};

export default BulkTagUntag;
